/** Misc barrel — notifications, tags, search, analytics, reports, jay, org. */
import client from './client';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function downloadCsvBlob(res, fallbackName) {
  const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || '';
  const m = cd.match(/filename="?([^";]+)"?/i);
  const filename = m ? m[1] : fallbackName;
  const blob = new Blob([res.data], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return filename;
}

function _qs(filters, columns, format) {
  const q = new URLSearchParams();
  Object.entries(filters || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, String(v));
  });
  if (columns?.length) q.append('columns', columns.join(','));
  q.append('format', format);
  return q.toString();
}

export const analyticsAPI = {
  getSkillWordcloud: async (industry = '', sector = '') => {
    const q = new URLSearchParams();
    if (industry) q.append('industry', industry);
    if (sector) q.append('sector', sector);
    return (await client.get(`/analytics/skill-wordcloud?${q.toString()}`)).data;
  },
  getCategorizedWordcloud: async (industry = '', category = '') => {
    const q = new URLSearchParams();
    if (industry) q.append('industry', industry);
    if (category) q.append('category', category);
    return (await client.get(`/analytics/skill-wordcloud-categorized?${q.toString()}`)).data;
  },
  getIndustries: async () => (await client.get('/analytics/industries')).data,
};

export const tagsAPI = {
  taxonomy: async () => (await client.get('/tags/taxonomy')).data,
  getMine: async () => (await client.get('/applicants/tags')).data,
  replace: async (tags) => (await client.put('/applicants/tags', { tags })).data,
  add: async (name, category) => (await client.post('/applicants/tags', { name, category })).data,
  remove: async (name) => (await client.delete(`/applicants/tags/${encodeURIComponent(name)}`)).data,
  retagJob: async (jobId) => (await client.post(`/jobs/${jobId}/retag`)).data,
  retagAllJobs: async () => (await client.post('/jobs/retag-all')).data,
  uploadResume: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return (await client.post('/applicants/upload-resume', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
};

export const searchAPI = {
  aiMatch: async (jd, req, minMatch = 50, page = 1, pageSize = 10) =>
    (await client.post('/search/ai-match', { job_description: jd, requirements: req, min_match_percentage: minMatch, page, page_size: pageSize })).data,
};

export const notificationAPI = {
  getNotifications: async (skip = 0, limit = 20) => (await client.get(`/notifications?skip=${skip}&limit=${limit}`)).data,
  getUnreadCount: async () => (await client.get('/notifications/unread-count')).data,
  markRead: async (id) => (await client.put(`/notifications/${id}/read`)).data,
  markAllRead: async () => (await client.put('/notifications/read-all')).data,
  dismiss: async (id) => (await client.delete(`/notifications/${id}`)).data,
  clearAll: async () => (await client.delete('/notifications')).data,
  connectWebSocket: (token) => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    return new WebSocket(`${wsUrl}/ws/notifications?token=${encodeURIComponent(token)}`);
  },
};

export const emailPrefsAPI = {
  getPrefs: async (token) => (await client.get(`/email-prefs/${token}`)).data,
  unsubscribe: async (token) => (await client.post(`/email-prefs/${token}/unsubscribe`)).data,
  resubscribe: async (token) => (await client.post(`/email-prefs/${token}/resubscribe`)).data,
};

export const supportAPI = {
  // Customer side
  createTicket: async (subject, body) => (await client.post('/support/tickets', { subject, body })).data,
  myTickets: async (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.append(k, String(v)); });
    return (await client.get(`/support/tickets/mine?${q.toString()}`)).data;
  },
  getTicket: async (id) => (await client.get(`/support/tickets/${id}`)).data,
  postMessage: async (id, body) => (await client.post(`/support/tickets/${id}/messages`, { body })).data,
  closeTicket: async (id) => (await client.post(`/support/tickets/${id}/close`)).data,
  // Support-agent side
  agentSignup: async (payload) => (await client.post('/support/agent/signup', payload)).data,
  agentInbox: async (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.append(k, String(v)); });
    return (await client.get(`/support/agent/inbox?${q.toString()}`)).data;
  },
  createOutboundTicket: async (payload) => (await client.post('/support/agent/outbound-ticket', payload)).data,
  // Admin
  adminListAgents: async (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.append(k, String(v)); });
    return (await client.get(`/admin/support-agents?${q.toString()}`)).data;
  },
  adminApproveAgent: async (id) => (await client.post(`/admin/support-agents/${id}/approve`)).data,
  adminRevokeAgent: async (id) => (await client.post(`/admin/support-agents/${id}/revoke`)).data,
  adminRestoreAgent: async (id) => (await client.post(`/admin/support-agents/${id}/restore`)).data,
  // Admin invite codes
  adminCreateInvite: async (payload) => (await client.post('/admin/support-agents/invite-codes', payload)).data,
  adminListInvites: async (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.append(k, String(v)); });
    return (await client.get(`/admin/support-agents/invite-codes?${q.toString()}`)).data;
  },
  adminRevokeInvite: async (id) => (await client.delete(`/admin/support-agents/invite-codes/${id}`)).data,
};

export const orgAPI = {
  getContext: async () => (await client.get('/org/me')).data,
  getMembers: async () => (await client.get('/org/members')).data,
  getPending: async () => (await client.get('/org/pending')).data,
  approve: async (userId) => (await client.post(`/org/members/${userId}/approve`)).data,
  reject: async (userId) => (await client.post(`/org/members/${userId}/reject`)).data,
  revoke: async (userId) => (await client.post(`/org/members/${userId}/revoke`)).data,
  restore: async (userId) => (await client.post(`/org/members/${userId}/restore`)).data,
  invite: async (email, name = '', position = 'Recruiter') => (await client.post('/org/invite', { email, name, position })).data,
  listInvitations: async () => (await client.get('/org/invitations')).data,
  cancelInvitation: async (id) => (await client.delete(`/org/invitations/${id}`)).data,
  previewInvite: async (token) => (await client.get(`/org/invite/${token}`)).data,
  acceptInvite: async (token, payload) => (await client.post(`/org/invite/${token}/accept`, payload)).data,
  getOrgJobs: async (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.append(k, String(v));
    });
    return (await client.get(`/org/jobs?${q.toString()}`)).data;
  },
};

export const reportsAPI = {
  recruiterColumns: async () => (await client.get('/reports/recruiter/columns')).data,
  recruiterPreview: async (filters, columns) =>
    (await client.get(`/reports/recruiter/jobs?${_qs(filters, columns, 'preview')}`)).data,
  recruiterDownload: async (filters, columns) => {
    const res = await client.get(`/reports/recruiter/jobs?${_qs(filters, columns, 'csv')}`, { responseType: 'blob' });
    return downloadCsvBlob(res, 'recruiter_jobs_report.csv');
  },
  adminColumns: async () => (await client.get('/reports/admin/columns')).data,
  adminPreview: async (filters, columns) =>
    (await client.get(`/reports/admin/jobs?${_qs(filters, columns, 'preview')}`)).data,
  adminDownload: async (filters, columns) => {
    const res = await client.get(`/reports/admin/jobs?${_qs(filters, columns, 'csv')}`, { responseType: 'blob' });
    return downloadCsvBlob(res, 'admin_jobs_report.csv');
  },
};

export const jayAPI = {
  chat: async (message) => (await client.post('/jay/chat', { message })).data,
  uploadAudio: async (interviewId, questionIndex, audioBlob) => {
    const formData = new FormData();
    formData.append('question_index', String(questionIndex));
    formData.append('audio', audioBlob, `q${questionIndex}.webm`);
    return (await client.post(`/interviews/${interviewId}/upload-audio`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })).data;
  },
};

// Re-export filesAPI for backward compat from miscAPI
export { filesAPI } from './filesAPI';

// Backend host (used by some components that build absolute URLs)
export { BACKEND };

export default {};
